<template>
  <div class="container page-global">
    <b-row>
      <b-col cols="12">
        <h1>Cashless ROI Calculators</h1>
        <p class="intro">
          Free, vendor-neutral tools to model the financial impact of going cashless.
          Start with payroll deduction for healthcare and senior living, corporate dining for workplace
          cafés, or participation levers when you need to compare multi-year profitability drivers.
        </p>
      </b-col>
    </b-row>

    <b-row class="calculator-grid">
      <b-col cols="12" md="4">
        <b-card class="calculator-card" title="Payroll Deduction ROI (Healthcare)">
          <p>
            Estimate the financial impact of adding or expanding payroll deduction for employees and staff
            in healthcare or senior living environments.
          </p>
          <ul>
            <li>Average ticket and transaction lift</li>
            <li>Cashless participation and traffic impact</li>
            <li>Payback period and annual profit increase</li>
          </ul>
          <router-link
            to="/calculators/payroll-deduction"
            class="calculator-link"
          >
            Open Calculator
          </router-link>
        </b-card>
      </b-col>

      <b-col cols="12" md="4">
        <b-card class="calculator-card" title="Corporate Dining ROI">
          <p>
            Evaluate the financial and engagement impact of cashless programs in corporate dining.
            Focus on retention, participation & data, and competing with off-site dining.
          </p>
          <ul>
            <li>Participation & data for menu optimization</li>
            <li>Retention vs. off-site dining competition</li>
            <li>Hybrid options for underbanked staff inclusivity</li>
          </ul>
          <router-link
            to="/calculators/corporate-dining"
            class="calculator-link"
          >
            Open Calculator
          </router-link>
        </b-card>
      </b-col>

      <b-col cols="12" md="4">
        <b-card class="calculator-card" title="Participation ROI">
          <p>
            Factor where to apply leverage in outreach. See how Participation %, Transaction Boost, and Labor Refinement
            impact Prime Cost and profitability over 1, 3, and 5 years.
          </p>
          <ul>
            <li>Prime Cost optimization & incremental revenue</li>
            <li>Sensitivity analysis: which lever has biggest impact</li>
            <li>RevPASH, LCPT, and Seconds Saved metrics</li>
          </ul>
          <router-link
            to="/calculators/participation"
            class="calculator-link"
          >
            Open Calculator
          </router-link>
        </b-card>
      </b-col>
    </b-row>

    <b-row>
      <b-col cols="12">
        <div class="notice">
          NOTICE: We do not make any warranties or claims about the usefulness or accuracy of these calculators.
          We strive for accuracy and if you discover an issue, please let us know. 
        </div>
      </b-col>
    </b-row>
  </div>
</template>

<script>
export default {
  name: "Calculators",
};
</script>

<style lang="scss" scoped>
@import '@/assets/css/app.scss';

h1 {
  font-size: 2.5rem;
  font-weight: 800;
  color: $body-color;
  margin-bottom: 1rem;
  text-align: center;
}

.intro {
  font-size: 1.1rem;
  color: rgba($body-color, 0.9);
  max-width: 800px;
  margin: 0 auto 2.5rem auto;
  text-align: center;
  line-height: 1.6;
}

.calculator-grid {
  margin-bottom: 2.5rem;
  align-items: stretch; // Ensure all cards have equal height
}

.calculator-card {
  background: lighten($body-bg, 5%);
  border-radius: 0.75rem;
  border: 1px solid rgba($body-color, 0.2);
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.15);
  height: 100%;
  display: flex;
  flex-direction: column;
  padding: 1.5rem;

  ::v-deep .card-body {
    display: flex;
    flex-direction: column;
    flex: 1;
    padding: 0;
    text-align: left; // ensure all inner content (including lists) is left aligned
  }

  ::v-deep .card-title {
    font-size: 1.5rem;
    font-weight: 700;
    color: $primary;
    margin-bottom: 1rem;
    line-height: 1.3;
  }

  p {
    font-size: 0.98rem;
    color: rgba($body-color, 0.9);
    line-height: 1.6;
    margin-bottom: 1rem;
    flex-grow: 0;
    text-align: left;
  }

  ul {
    margin-top: 0;
    margin-bottom: 1.5rem;
    padding-left: 1.2rem;
    font-size: 0.95rem;
    color: rgba($body-color, 0.85);
    line-height: 1.6;
    flex-grow: 0;
    text-align: left;

    li {
      margin-bottom: 0.5rem;
    }
  }

  .calculator-link {
    margin-top: auto;
    align-self: flex-start;
    padding: 0.75rem 1.5rem;
    background: $primary;
    color: $body-bg;
    border-radius: 0.5rem;
    font-weight: 600;
    text-decoration: none;
    transition: background 0.2s ease, transform 0.1s ease, box-shadow 0.2s ease;
    display: inline-block;

    &:hover {
      background: lighten($primary, 10%);
      color: $body-bg;
      text-decoration: none;
      transform: translateY(-1px);
      box-shadow: 0 3px 8px rgba($primary, 0.4);
    }
  }

  .coming-soon-tag {
    margin-top: auto;
    align-self: flex-start;
    padding: 0.4rem 0.8rem;
    border-radius: 999px;
    border: 1px dashed rgba($body-color, 0.4);
    color: $secondary;
    font-size: 0.85rem;
    display: inline-block;
  }
}

.notice {
  color: $primary;
  font-size: 1rem;
  text-align: left;
  padding: 1.25rem;
  background: lighten($body-bg, 5%);
  border-radius: 0.5rem;
  max-width: 800px;
  margin: 2rem auto 0 auto;
  line-height: 1.6;
  border: 1px solid rgba($primary, 0.2);
}
</style>